import { withConflictConfirm } from '../availability/conflicts';
import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { pickUrl } from '../catalog/pick';
import { useSupplierSwitch } from './supplierSwitch';
import { nameNeedRem, quoteTableMinRem } from './columns';
import { api, ApiError, fieldIssues } from '../api';
import { useMe } from '../auth/useMe';
import { RowMenu } from '../ui/RowMenu';
import { blockRights, InsertBlockDialog } from './InsertBlockDialog';
import { SheetAddRow, SheetSection } from '../ui/Sheet';
import { DialogLayer, useEscapeLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { AddPricedLine, type AddGear, type ReplacedLine } from './AddPricedLine';
import { ReplaceLine } from './ReplaceLine';
import { formatPln } from '../catalog/money';
import { QuoteLineRow, type Columns } from './QuoteLineRow';
import { lineTarget, mayOpen, useCatalogOpener } from '../nav/catalogLink';
import { discountShown, quoteParts, sectionSuggestions, sectionSum, shownSections, supplierShown } from './parts';
import { useCategories } from '../catalog/ItemForm';
import { LINES_RESOURCE, type Placed, type QuoteLine, type Section, type Version } from './types';
import { PlacementNotice } from './PlacementNotice';
import { can, fieldWritable } from '../auth/me';
import { NewCatalogItemDialog, type NewItemSeed } from './NewCatalogItemDialog';
import { OTHER_TITLE, useFixedServices } from './fixedServices';
import { dropOn, edgeMove, landing, moveBy, putAt, restoreBody, sectionOrderBody, undoable, withSectionOrder, type CrossMove } from './lineOrder';
import { useRowGuard } from '../ui/unsaved';
import { RentalForm } from '../subrentals/RentalForm';
import { RENTALS_RESOURCE, type RentalPreset } from '../subrentals/types';

/**
 * "+ Usługa" / "+ Transport" of a fixed section (P8, note 22:46): the price list's services (transports in
 * "Transport", the others in "Obsługa") to pick, without typing; the server puts the line in its fixed section.
 */
function FixedSectionAdd({ versionId, section, onAdded }: { versionId: string; section: Section; /** `other`: „Inne” — its name takes the focus */ onAdded: (line: QuoteLine, other: boolean) => void }) {
  const transport = section.kind === 'transport';
  const { ready, list, labelOf } = useFixedServices(versionId, transport);
  const [error, setError] = useState('');
  async function post(body: Record<string, unknown>, other = false) {
    try {
      onAdded(await api<QuoteLine>(`/api/quotes/versions/${versionId}/lines`, { method: 'POST', body: { ...body, sectionId: section.id, quantity: 1 } }), other);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się dodać');
    }
  }
  const add = (h: { id: string }) => post({ type: 'service', serviceId: h.id });
  // owner 19:55: „Inne” at the end of the list — an own line in this section, its name to type (the server needs a
  // name: „Inne”, selected, so typing replaces it), the price filled in on the line (0 until then)
  const addOther = () => post({ type: 'custom', name: 'Inne', unitPriceGrosze: 0 }, true);
  return (
    <span className="q-fixed-add">
      {ready && (
        <RowMenu
          text={transport ? '+ Transport' : '+ Usługa'}
          label={transport ? 'Dodaj transport z cennika' : 'Dodaj usługę z cennika'}
          featureId="quotes.fixed_sections"
          floating
          actions={[...list.map((h) => ({ label: labelOf(h), onSelect: () => void add(h) })), { label: 'Inne', title: OTHER_TITLE, onSelect: () => void addOther() }]}
        />
      )}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

type Handlers = {
  onLineSaved: (line: QuoteLine) => void;
  onLineAdded: (line: QuoteLine) => void;
  onLineDeleted: (lineId: string) => void;
  /** Owner 2026-10-24 16:15: a line replaced in place by another (`replacedId` is gone). */
  onLineReplaced: (line: ReplacedLine) => void;
  onPreview: (lineId: string, net: number | null) => void;
  /** Something changed on the server that this screen does not hold (a section, a conflict): read it again. */
  onRefresh: () => void;
  /** The whole version as the server sent it after a change of the lines' order. */
  onReordered: (version: Version) => void;
  /** Owner 2026-10-23 20:25: the notice about the category of the line just added ("Przenieś" / "Cofnij"), or null. */
  placed?: Placed | null;
  onPlacedClose?: () => void;
  /** „Utwórz sekcję i przenieś” made the section: the line's „Cofnij” note instead. */
  onPlaced?: (next: Placed) => void;
};

/** A line just deleted (× or Delete), for „Cofnij”: its place in its section. */
type Removed = { line: QuoteLine; index: number };
/** What the question „Usunąć …?” is about (owner 19:48). */
type Asking = { lines: QuoteLine[] } | { section: Section };

/** „pozycję” / „pozycje” / „pozycji” after a number (2 pozycje, 5 pozycji, 22 pozycje). */
export function linesWord(n: number): string {
  if (n === 1) return 'pozycję';
  const last = n % 10;
  const tens = n % 100;
  return last >= 2 && last <= 4 && (tens < 12 || tens > 14) ? 'pozycje' : 'pozycji';
}
/** Owner 2026-10-24 10:47 („jesli zmieniam to pytaj czy napewno”): the question before a line goes to another section. */
const moveTitle = (m: CrossMove, lines: QuoteLine[], section: Section | undefined) =>
  `Przenieść ${m.lineIds.length === 1 ? `„${lines[0]?.name ?? ''}”` : `${m.lineIds.length} ${linesWord(m.lineIds.length)}`} do sekcji „${section?.name ?? ''}”?`;
const askTitle = (a: Asking) =>
  'section' in a ? `Usunąć sekcję „${a.section.name}”?` : a.lines.length === 1 ? `Usunąć pozycję „${a.lines[0]!.name}”?` : `Usunąć ${a.lines.length} ${linesWord(a.lines.length)}?`;

/** Clicks that keep the selection (19:46): on a line or the selected section, in the question, a menu, „Cofnij”. */
const KEEPS_SELECTION = 'tr[data-line-id], tr[data-section-id], [role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], .q-undo';
/** A click on a control of the empty section's row is the control's (as SheetAddRow's). */
const OWN_CLICK = 'input, textarea, select, button, a[href], [role="listbox"], [role="option"], [role="menu"], [role^="menuitem"]';

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.status === 409 ? 'Ktoś w tym czasie zmienił kosztorys. Pokazuję aktualny stan.' : (fieldIssues(e)[0]?.message ?? e.message)) : fallback;

/** The section's name in the first column; in a draft it is edited in place (Enter or leaving saves). */
function SectionName({ section, editable, onRefresh }: { section: Section; editable: boolean; onRefresh: () => void }) {
  const [name, setName] = useState(section.name);
  const [error, setError] = useState('');
  useEffect(() => setName(section.name), [section.name]);
  // owner 2026-10-23 21:14: a name typed and not saved: leaving waits for its save or asks first
  const guard = useRowGuard(editable && name.trim() !== section.name, async () => {
    if (name.trim() === section.name) return (setName(section.name), true);
    try {
      await api(`/api/quotes/sections/${section.id}`, { method: 'PATCH', body: { name } });
      setError('');
      onRefresh();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać');
      return false;
    }
  });
  if (!editable) return <span className="sheet-section-name q-text">{section.name}</span>;
  const save = guard.save;
  return (
    <>
      <textarea
        ref={guard.ref}
        rows={1}
        className="cell-input sheet-section-name"
        aria-label={`Nazwa sekcji: ${section.name}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save();
          } else if (e.key === 'Escape' && name !== section.name) {
            e.preventDefault();
            e.stopPropagation();
            setName(section.name);
          }
        }}
      />
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}

/**
 * The quote as a table, grouped by sections like the owner's PDF: Urządzenie / Usługa (the section) | Marka | Cena |
 * Ilość | Ilość dni | Rabat | Kwota netto | Opis. Amount columns only for a role that may see them. In a draft
 * every cell is editable and each section ends with the quick picker.
 */
export function QuoteTable({
  version,
  editable,
  cols,
  suppliers,
  eventId,
  ...h
}: { version: Version; editable: boolean; cols: Columns; /** may work with suppliers (B1–B4) */ suppliers?: boolean; /** „Dopożycz” of a shortage (owner 21:05) needs the event */ eventId?: string } & Handlers) {
  const { me } = useMe();
  // owner 2026-10-23 21:05: „Dopożycz” under a shortage — the packlist's form, the item and the missing quantity filled in
  const [subrent, setSubrent] = useState<RentalPreset | null>(null);
  const maySubrent = !!eventId && !!me && can(me, RENTALS_RESOURCE, 'create');
  const onSubrent = maySubrent
    ? (l: QuoteLine) => l.itemId && l.shortage && setSubrent({ itemId: l.itemId, itemName: l.name, quantity: l.shortage.missing, firstDay: l.shortage.firstDay, lastDay: l.shortage.lastDay })
    : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  // owner 2026-10-23 20:53: Ctrl+klik (Windows: Alt+klik) on a line opens it in the catalog with the editor open
  const openInCatalog = useCatalogOpener();
  const [useSuppliers, setUseSuppliers] = useSupplierSwitch();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  // owner 20:41: the red message under the table goes away with a click anywhere (but on itself)
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!error) return;
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && errorRef.current?.contains(e.target)) return;
      setError('');
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [error]);
  // F26 (owner 4/33): "Wstaw blok"; 20:00: in the ⚙ by the "Dodaj…" field (the block goes where it always went)
  const [inserting, setInserting] = useState(false);
  const [notice, setNotice] = useState('');
  const mayInsertBlock = editable && !!me && (blockRights(me).insertQuote || blockRights(me).insertEquipment);
  // P8 (point 27, note 22:50): an own line → the catalog, or a new catalog item from the quick picker
  const [toCatalog, setToCatalog] = useState<null | { seed: NewItemSeed; line?: QuoteLine; sectionId: string; quantity: number }>(null);
  const mayAddToCatalog = editable && !!me && can(me, 'catalog.items', 'create');
  async function catalogItemCreated(itemId: string) {
    const c = toCatalog!;
    try {
      // gear other events reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24); „Anuluj” leaves the line as it was
      const line = c.line
        ? await withConflictConfirm((confirmConflicts) => api<QuoteLine>(`/api/quotes/lines/${c.line!.id}/link-item`, { method: 'POST', body: { itemId, version: c.line!.version, ...(confirmConflicts ? { confirmConflicts } : {}) } }))
        : await api<QuoteLine>(`/api/quotes/versions/${version.id}/lines`, { method: 'POST', body: { type: 'item', itemId, sectionId: c.sectionId, quantity: c.quantity } });
      setToCatalog(null);
      if (!line) return;
      setError('');
      if (c.line) h.onLineSaved(line);
      else h.onLineAdded(line);
    } catch (e) {
      // the item is in the catalog already; only the quote's line did not follow
      setError(e instanceof ApiError ? `Pozycja jest już w katalogu, ale wiersza nie zmieniono: ${e.message}` : 'Nie udało się zmienić wiersza');
      h.onRefresh();
    }
    setToCatalog(null);
  }
  // P8 (point 10): "+ Sekcja" suggests the catalog's categories (only for whoever may see them)
  const categories = useCategories(editable && adding && !!me && can(me, 'catalog.categories', 'view')).data?.categories ?? [];
  const suggestions = sectionSuggestions(categories, version.sections);
  // P8 (point 9): "Rabat %" only once something has a discount
  // owner 2026-10-21: "Dostawca" only once the first line from a supplier is there
  const supplierCol = !!cols.cost && supplierShown(version);
  const shownCols: Columns = { ...cols, discountHidden: !discountShown(version), supplier: supplierCol, supplierEditable: supplierCol && !!suppliers && !!cols.costEditable };
  const discountCol = cols.discount && !shownCols.discountHidden;
  // owner 16:22: "Marka" grows for a long title (columns.ts nameNeedRem); the "Dodaj…" field spans it
  const nameRem = nameNeedRem(version.sections.flatMap((s) => s.lines.map((l) => l.name)));
  /** The gear of a section's new-line field: "Wybierz z katalogu" (our catalog or, with the switch on, the suppliers'). */
  const gearOf = (sectionId: string): AddGear => ({
    canSuppliers: !!suppliers,
    useSuppliers: !!suppliers && useSuppliers,
    onToggleSuppliers: () => setUseSuppliers(!useSuppliers),
    onInsertBlock: mayInsertBlock ? () => setInserting(true) : undefined,
    onPickCatalog: () => {
      const back = `${location.pathname}${location.search}`;
      navigate(pickUrl(suppliers && useSuppliers ? '/katalog/dostawcy' : '/katalog', { versionId: version.id, sectionId, back }));
    },
  });
  // part totals: the label over every column before "Kwota netto" (section, Marka, Cena, Ilość, Ilość dni, Rabat)
  const beforeNet = 4 + (cols.price ? 1 : 0) + (discountCol ? 1 : 0);
  // section, Marka, Ilość, Ilość dni, Opis + the optional ones
  const columnCount = 5 + (cols.price ? 1 : 0) + (discountCol ? 1 : 0) + (cols.net ? 1 : 0) + (cols.cost ? 1 : 0) + (supplierCol ? 1 : 0) + (editable ? 1 : 0);
  // after "Kwota netto" on the total rows: Koszt, Dostawca, Opis and the actions
  const afterNet = 1 + (cols.cost ? 1 : 0) + (supplierCol ? 1 : 0) + (editable ? 1 : 0);

  // ---------- owner 2026-10-23 18:34: selected lines, Delete with „Cofnij”, the order within a section ----------
  // only in a draft on a desktop (`editable`; the phone never edits): the keys act while the focus is on a row.
  // 19:46: Shift+click a range, ⌘ (Ctrl) +click one in or out; a click on empty space clears; an empty section is
  // selected like a line. 19:48: every delete asks first; Esc with a selection only clears it (the card stays).
  // 19:50: „Obsługa” and „Transport” lines alike.
  const [selected, setSelected] = useState<string[]>([]);
  const [selSection, setSelSection] = useState<string | null>(null);
  const anchor = useRef<string | null>(null);
  const [removed, setRemoved] = useState<Removed[]>([]);
  const [asking, setAsking] = useState<Asking | null>(null);
  /** The order shown while the server is saving it (sectionId → line ids). */
  const [localOrder, setLocalOrder] = useState<Record<string, string[]>>({});
  /** The dragged line(s): `ids` in their order (several: the selected lines of one equipment section, moved together
   *  only into another section); `cross`: from an equipment section, so another equipment section takes it (10:47). */
  const [drag, setDrag] = useState<{ sectionId: string; id: string; ids: string[]; cross: boolean } | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; after: boolean } | null>(null);
  /** An empty equipment section a line would land in (its own row shows the place). */
  const [dropEnd, setDropEnd] = useState<string | null>(null);
  /** The move into another section waiting for „Przenieś” / „Anuluj”. */
  const [moving, setMoving] = useState<CrossMove | null>(null);
  const moveButton = useRef<HTMLButtonElement>(null);
  // owner 2026-10-23 20:46: whole equipment sections move; 2026-10-24 16:15 („ten chwytak po prawej eliminujemy”): no
  // ⋮⋮ of their own any more — Ctrl (⌘ on a Mac) + a drag of the FIRST line's ⋮⋮ moves the whole section, a bubble
  // says so; Ctrl+⌥↑ / Ctrl+⌥↓ on that line too (an empty section: selected by a click, ⌥↑ / ⌥↓ as before)
  /** The equipment sections' order shown while the server is saving it. */
  const [localSections, setLocalSections] = useState<string[] | null>(null);
  const wantedSections = useRef<string[] | null>(null);
  const [secDrag, setSecDrag] = useState<string | null>(null);
  const [secDropAt, setSecDropAt] = useState<{ id: string; after: boolean } | null>(null);
  /** The bubble „Przenosisz całą sekcję „…”” by the pointer while a whole section is dragged. */
  const [secTip, setSecTip] = useState<{ name: string; x: number; y: number } | null>(null);
  /** An empty section whose row takes the focus after the next render (it moved: its row was put elsewhere). */
  const focusSection = useRef<string | null>(null);
  const sheet = useRef<HTMLDivElement>(null);
  /** A line whose row takes the focus after the next render (null: the notice „Usunięto …”); undefined: nothing. */
  const focusTo = useRef<string | null | undefined>(undefined);
  const focusName = useRef<string | null>(null);
  /** Owner 2026-10-24 16:15: the line whose name was clicked — its change to another line is open in its row. */
  const [replacing, setReplacing] = useState<string | null>(null);
  /**
   * Review 2: the change is offered only to whoever may write what it writes (the server's rule 7a): the line's name,
   * price, „Liniowo” and transport; a supplier's line also needs the rentals' delete (its rental goes). Anyone else
   * clicks the name as any other text of the row (it selects).
   */
  const mayReplace = (l: QuoteLine): boolean =>
    editable && !!me && cols.price && ['name', 'unitPriceGrosze', 'linear', 'transport'].every((f) => fieldWritable(me, LINES_RESOURCE, f)) && (!l.supplierId || can(me, RENTALS_RESOURCE, 'delete'));
  function lineReplaced(line: ReplacedLine, other: boolean) {
    setReplacing(null);
    // the line stays selected under its new id; „Inne” gets its name typed (as when added)
    setSelected((cur) => cur.map((id) => (id === line.replacedId ? line.id : id)));
    if (anchor.current === line.replacedId) anchor.current = line.id;
    if (other) focusName.current = line.id;
    else focusTo.current = line.id;
    h.onLineReplaced(line);
  }
  /** Owner 2026-10-24 11:03: the category note of the line just added, in its own row (under its name). */
  const placementOf = (l: QuoteLine) =>
    editable && h.placed?.lineId === l.id ? (
      <PlacementNotice
        key={`${h.placed.kind}-${l.id}`}
        placed={h.placed}
        // review I2: the line's version now (it may have been edited since it was added)
        lineVersion={l.version}
        onClose={() => h.onPlacedClose?.()}
        onMoved={(line) => {
          h.onLineSaved(line);
          // the moved line is selected in its new section
          setSelSection(null);
          setSelected([line.id]);
          anchor.current = line.id;
          focusTo.current = line.id;
        }}
        onPlaced={(next) => {
          h.onPlaced?.(next);
          // the new section is not on this screen yet
          h.onRefresh();
        }}
        onUndone={h.onRefresh}
      />
    ) : undefined;
  /**
   * The first line added from an empty section's own "Dodaj…" row: that row gives way to the section's add row under the
   * line (a new field), so the focus moves there and the keyboard goes on („2 qrp40”, Enter, the next one).
   */
  const focusAdd = useRef<string | null>(null);
  const wanted = useRef<Record<string, string[]>>({});
  // the changes of order (and „Cofnij”) one after another: each reads the lines' versions fresh before it saves
  const queue = useRef<Promise<void>>(Promise.resolve());
  const ordered = (sec: Section): QuoteLine[] => {
    const ids = localOrder[sec.id];
    if (!ids) return sec.lines;
    const byId = new Map(sec.lines.map((l) => [l.id, l]));
    return [...ids.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])), ...sec.lines.filter((l) => !ids.includes(l.id))];
  };
  const shown = withSectionOrder(shownSections(version, editable), localSections).map((sec) => ({ ...sec, lines: ordered(sec) }));
  const equipmentIds = shown.filter((x) => x.kind === 'equipment').map((x) => x.id);
  const parts = quoteParts({ ...version, sections: shown }, shownCols);
  const flat = parts.flatMap((p) => p.sections.flatMap((sec) => sec.lines.map((line) => ({ line, section: sec }))));
  const hasSelection = selected.length > 0 || selSection !== null;
  const clearSelection = () => {
    setSelected([]);
    setSelSection(null);
  };
  // the server shows the order asked for: the local one is no longer needed
  useEffect(() => {
    setLocalOrder((cur) => {
      const next = { ...cur };
      let changed = false;
      for (const [id, ids] of Object.entries(cur)) {
        const sec = version.sections.find((x) => x.id === id);
        if (!sec || sec.lines.map((l) => l.id).join() === ids.join()) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : cur;
    });
    setLocalSections((cur) => (cur && version.sections.filter((x) => x.kind === 'equipment').map((x) => x.id).join() === cur.join() ? null : cur));
  }, [version]);
  useEffect(() => {
    const id = focusSection.current;
    const row = id ? sheet.current?.querySelector<HTMLElement>(`tr[data-section-id="${id}"]`) : null;
    if (!row) return;
    focusSection.current = null;
    row.focus({ preventScroll: true });
  });
  useEffect(() => {
    const id = focusAdd.current;
    const field = id ? sheet.current?.querySelector<HTMLInputElement>(`tbody[data-sheet-section="${id}"] tr.sheet-add input[role="combobox"]`) : null;
    if (!field) return;
    focusAdd.current = null;
    field.focus();
  });
  // „Inne” (19:55): the new line's name takes the focus, selected, once its row is there
  useEffect(() => {
    const id = focusName.current;
    const name = id ? sheet.current?.querySelector<HTMLTextAreaElement>(`tr[data-line-id="${id}"] textarea[aria-label^="Nazwa: "]`) : null;
    if (!name) return;
    focusName.current = null;
    name.focus();
    name.select();
  });
  useEffect(() => {
    if (focusTo.current === undefined) return;
    const id = focusTo.current;
    focusTo.current = undefined;
    const row = id ? sheet.current?.querySelector<HTMLElement>(`tr[data-line-id="${id}"]`) : null;
    // no line left to select: the notice „Usunięto … — Cofnij” holds the focus (⌘Z still reaches the quote)
    (row ?? sheet.current?.querySelector<HTMLElement>('.q-undo'))?.focus({ preventScroll: !row });
  });
  // 19:48: Esc with a selection only clears it — a layer over the card's own (ui/Dialog.tsx), so the card stays open
  useEscapeLayer(clearSelection, editable && hasSelection);
  // 19:46: a click anywhere outside the lines (empty table space, around the table, the card's margins) clears it;
  // a line's own controls and what the selection opened (the question, a menu, „Cofnij”) keep it
  useEffect(() => {
    if (!editable || !hasSelection) return;
    const onClick = (e: MouseEvent) => {
      if (e.composedPath().some((n) => n instanceof Element && n.matches(KEEPS_SELECTION))) return;
      clearSelection();
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [editable, hasSelection]);
  const later = (work: () => Promise<void>) => {
    queue.current = queue.current.then(work).catch((e: unknown) => {
      setError(errorText(e, 'Nie udało się zmienić kolejności'));
      setLocalOrder({});
      setLocalSections(null);
      h.onRefresh();
    });
  };
  /** Saves `ids` as the order of the section, with the versions of its lines as they are now on the server. */
  async function saveOrder(sectionId: string, ids: (now: QuoteLine[]) => string[] | undefined) {
    const fresh = await api<Version>(`/api/quotes/versions/${version.id}`);
    const sec = fresh.sections.find((x) => x.id === sectionId);
    const want = sec ? ids(sec.lines) : undefined;
    if (!sec || !want) return;
    const byId = new Map(sec.lines.map((l) => [l.id, l]));
    if (want.length !== sec.lines.length || want.some((id) => !byId.has(id))) throw new ApiError(409, 'conflict', 'changed');
    if (want.join() === sec.lines.map((l) => l.id).join()) return h.onReordered(fresh);
    h.onReordered(await api<Version>(`/api/quotes/sections/${sectionId}/order`, { method: 'PUT', body: { lines: want.map((id) => ({ id, version: byId.get(id)!.version })) } }));
  }
  function reorder(sectionId: string, next: string[] | null) {
    if (!next) return;
    wanted.current[sectionId] = next;
    setLocalOrder((cur) => ({ ...cur, [sectionId]: next }));
    later(() => saveOrder(sectionId, () => wanted.current[sectionId]));
  }
  /**
   * „Przenieś” (10:47): the lines one by one through their PATCH (`sectionId` + `beforeLineId`, the versions read fresh),
   * so they keep their order before the same line (or at the end); then the version as the server has it, the moved
   * lines selected there. A refusal stops: the screen is read again (`later`).
   */
  function moveLines(m: CrossMove) {
    later(async () => {
      const fresh = await api<Version>(`/api/quotes/versions/${version.id}`);
      const byId = new Map(fresh.sections.flatMap((x) => x.lines).map((l) => [l.id, l]));
      for (const id of m.lineIds) {
        const line = byId.get(id);
        if (!line) throw new ApiError(409, 'conflict', 'changed');
        await api<QuoteLine>(`/api/quotes/lines/${id}`, { method: 'PATCH', body: { sectionId: m.sectionId, beforeLineId: m.beforeLineId, version: line.version } });
      }
      h.onReordered(await api<Version>(`/api/quotes/versions/${version.id}`));
      setError('');
      setSelSection(null);
      setSelected(m.lineIds);
      anchor.current = m.lineIds[0] ?? null;
      focusTo.current = m.lineIds[0] ?? null;
    });
  }
  // the question's „Przenieś” takes the focus only after the drop is over (Safari puts it back otherwise, as #110)
  useEffect(() => {
    if (!moving) return;
    const t = window.setTimeout(() => moveButton.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [moving]);
  /** Saves the wanted order of the equipment sections, with their sorts as they are now on the server. */
  async function saveSectionOrder() {
    const want = wantedSections.current;
    if (!want) return;
    const fresh = await api<Version>(`/api/quotes/versions/${version.id}`);
    const body = sectionOrderBody(fresh.sections, want);
    if (!body) throw new ApiError(409, 'conflict', 'changed');
    if (fresh.sections.filter((x) => x.kind === 'equipment').map((x) => x.id).join() === want.join()) return h.onReordered(fresh);
    h.onReordered(await api<Version>(`/api/quotes/versions/${version.id}/sections/order`, { method: 'PUT', body }));
  }
  function reorderSections(next: string[] | null) {
    if (!next) return;
    wantedSections.current = next;
    setLocalSections(next);
    later(saveSectionOrder);
  }
  function selectLine(id: string, keys: { range: boolean; toggle: boolean }) {
    setSelSection(null);
    const from = anchor.current ? flat.findIndex((x) => x.line.id === anchor.current) : -1;
    if (keys.range && from >= 0) {
      // the anchor stays: the next Shift+click measures from it again
      const to = flat.findIndex((x) => x.line.id === id);
      setSelected(flat.slice(Math.min(from, to), Math.max(from, to) + 1).map((x) => x.line.id));
      return;
    }
    anchor.current = id;
    if (keys.toggle) setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    else setSelected([id]);
  }
  /** Delete / ×: the question first (19:48) — the selected lines, or the selected empty section. */
  function askDelete() {
    if (selSection) {
      const s = shown.find((x) => x.id === selSection);
      // only an empty section of the equipment (a section with lines is emptied first)
      if (s && s.kind === 'equipment' && s.lines.length === 0) setAsking({ section: s });
      return;
    }
    const lines = flat.filter((x) => selected.includes(x.line.id)).map((x) => x.line);
    if (lines.length > 0) setAsking({ lines });
  }
  /**
   * The lines deleted one by one through the same `DELETE` as ×, in the order shown. A line already gone (the tolls
   * leave with the last transport) is skipped; any other refusal stops there: what went is said, the screen is read again.
   */
  async function deleteLines(list: QuoteLine[]) {
    const gone: Removed[] = [];
    let failed: unknown = null;
    for (const line of list) {
      try {
        await api(`/api/quotes/lines/${line.id}?version=${line.version}`, { method: 'DELETE' });
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) continue;
        failed = e;
        break;
      }
      const hit = flat.find((x) => x.line.id === line.id);
      gone.push({ line, index: hit ? hit.section.lines.findIndex((l) => l.id === line.id) : 0 });
      h.onLineDeleted(line.id);
    }
    if (gone.length > 0) setRemoved(gone);
    // the selection goes to the next line that stays (or the one before)
    const ids = new Set(gone.map((r) => r.line.id));
    const last = Math.max(...gone.map((r) => flat.findIndex((x) => x.line.id === r.line.id)), -1);
    const next = flat.slice(last + 1).find((x) => !ids.has(x.line.id)) ?? [...flat.slice(0, Math.max(last, 0))].reverse().find((x) => !ids.has(x.line.id));
    if (failed) {
      const why = errorText(failed, 'Nie udało się usunąć');
      setError(gone.length > 0 ? `Usunięto ${gone.length} z ${list.length}. ${why}` : why);
      clearSelection();
      h.onRefresh();
      return;
    }
    setError('');
    setSelected(next ? [next.line.id] : []);
    anchor.current = next?.line.id ?? null;
    focusTo.current = next?.line.id ?? null;
  }
  /** „Cofnij”: the lines added again (their fields, the transport's method) and put back in their places. */
  function undo() {
    const list = removed.filter((r) => undoable(r.line));
    if (list.length === 0) return;
    setRemoved([]);
    later(async () => {
      const back: string[] = [];
      for (const r of list) {
        const added = await api<QuoteLine>(`/api/quotes/versions/${version.id}/lines`, { method: 'POST', body: restoreBody(r.line) });
        let line = added;
        const mode = r.line.transport?.chosenMode;
        if (mode) {
          const price = r.line.priceOverridden && r.line.unitPriceGrosze !== undefined ? { unitPriceGrosze: r.line.unitPriceGrosze } : {};
          line = await api<QuoteLine>(`/api/quotes/lines/${added.id}`, { method: 'PATCH', body: { transportMode: mode, version: added.version, ...price } });
        }
        h.onLineAdded(line);
        back.push(added.id);
        await saveOrder(added.sectionId, (now) => putAt(now.map((l) => l.id), added.id, r.index));
      }
      setError('');
      setSelSection(null);
      setSelected(back);
      anchor.current = back[0] ?? null;
      focusTo.current = back[0] ?? null;
    });
  }
  function onSheetKey(e: KeyboardEvent<HTMLDivElement>) {
    if (!editable || e.nativeEvent.isComposing) return;
    const target = e.target as Element;
    // a field, a list or a menu keeps its keys
    if (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]')) return;
    const mac = isMac();
    const command = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
    if (command && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
      if (removed.some((r) => undoable(r.line))) {
        e.preventDefault();
        undo();
      }
      return;
    }
    // owner 16:15: Ctrl+⌥↑ / Ctrl+⌥↓ (⌘ too) on the FIRST line of an equipment section move the whole section
    if ((e.ctrlKey || e.metaKey) && e.altKey && !e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && target.matches('tr[data-line-id]')) {
      const id = target.getAttribute('data-line-id');
      const sec = shown.find((x) => x.kind === 'equipment' && x.lines[0]?.id === id);
      if (!sec || !id) return;
      e.preventDefault();
      focusTo.current = id;
      reorderSections(moveBy(equipmentIds, sec.id, e.key === 'ArrowUp' ? -1 : 1));
      return;
    }
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    // the keys of the selection: only with the focus on a row of the quote
    if (!hasSelection || !target.matches('tr[data-line-id], tr[data-section-id]')) return;
    if (e.key === 'Escape' && !e.altKey) {
      e.preventDefault(); // taken: only the selection goes (the card never closes on Escape)
      clearSelection();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && !e.altKey) {
      e.preventDefault();
      askDelete();
    } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && selSection !== null) {
      // owner 20:46: ⌥↑ / ⌥↓ move the selected (empty, since 16:15) section among the equipment sections
      if (!e.altKey || !equipmentIds.includes(selSection)) return;
      e.preventDefault();
      focusSection.current = selSection;
      reorderSections(moveBy(equipmentIds, selSection, e.key === 'ArrowUp' ? -1 : 1));
    } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && selSection === null) {
      e.preventDefault();
      const at = flat.findIndex((x) => x.line.id === target.getAttribute('data-line-id'));
      const hit = flat[at];
      if (!hit) return;
      const delta = e.key === 'ArrowUp' ? -1 : 1;
      // the order moves for exactly one selected line (19:46): with several, nothing
      if (e.altKey) {
        if (selected.length !== 1 || selected[0] !== hit.line.id) return;
        const next = moveBy(hit.section.lines.map((l) => l.id), hit.line.id, delta);
        // 10:47: at the edge of an equipment section it goes on into the neighbouring one, after the question
        if (next) reorder(hit.section.id, next);
        else if (hit.section.kind === 'equipment') {
          const m = edgeMove(shown, hit.line.id, delta);
          if (m) setMoving(m);
        }
      } else {
        const next = flat[at + delta];
        if (next) {
          setSelected([next.line.id]);
          anchor.current = next.line.id;
          focusTo.current = next.line.id;
        }
      }
    }
  }
  /** Owner 16:15: the first line of an equipment section carries the whole section with Ctrl (⌘ on a Mac). */
  const leads = (sec: Section, l: QuoteLine) => sec.kind === 'equipment' && sec.lines[0]?.id === l.id;
  /** The whole section dragged from its first line's ⋮⋮: its rows as the drag image, the bubble by the pointer. */
  function startSectionDrag(e: DragEvent<HTMLElement>, sec: Section) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sec.id);
    const body = e.currentTarget.closest('tbody');
    if (body) e.dataTransfer.setDragImage(body, 16, 12);
    setSecDrag(sec.id);
    setSecTip({ name: sec.name, x: e.clientX, y: e.clientY });
  }
  const endDrag = () => {
    setDrag(null);
    setDropAt(null);
    setDropEnd(null);
    setSecDrag(null);
    setSecDropAt(null);
    setSecTip(null);
  };
  /**
   * While dragging: the bubble follows the pointer; Ctrl / ⌘ pressed only after the first line's drag began turns it
   * into the whole section's (the drag image stays the line's — the browser fixes it at the start).
   */
  function onSheetDragOver(e: DragEvent<HTMLDivElement>) {
    if (!editable) return;
    if (drag && drag.ids.length === 1 && (e.ctrlKey || e.metaKey)) {
      const sec = shown.find((x) => x.id === drag.sectionId);
      const line = sec?.lines.find((l) => l.id === drag.id);
      if (sec && line && leads(sec, line)) {
        setDrag(null);
        setDropAt(null);
        setDropEnd(null);
        setSecDrag(sec.id);
        setSecTip({ name: sec.name, x: e.clientX, y: e.clientY });
        return;
      }
    }
    if (secTip && (secTip.x !== e.clientX || secTip.y !== e.clientY) && (e.clientX !== 0 || e.clientY !== 0)) setSecTip({ ...secTip, x: e.clientX, y: e.clientY });
  }
  const selectOf = (sec: Section, l: QuoteLine) =>
    editable
      ? {
          selected: selected.includes(l.id),
          onSelect: (keys: { range: boolean; toggle: boolean }) => selectLine(l.id, keys),
          dragging: !!drag?.ids.includes(l.id),
          drop: dropAt?.id === l.id && drag?.id !== l.id ? (dropAt.after ? ('after' as const) : ('before' as const)) : null,
          grip: (
            <span
              className="q-grip"
              draggable
              aria-hidden="true"
              title={
                sec.kind === 'equipment'
                  ? `Przeciągnij, żeby zmienić kolejność albo przenieść do innej sekcji (albo ⌥↑ / ⌥↓ na zaznaczonej pozycji)${leads(sec, l) ? '\nCtrl+przeciągnij: przenieś całą sekcję' : ''}`
                  : 'Przeciągnij, żeby zmienić kolejność w sekcji (albo ⌥↑ / ⌥↓ na zaznaczonej pozycji)'
              }
              data-feature-id={leads(sec, l) ? 'quotes.section_order' : 'quotes.line_order'}
              // Safari on a Mac: Ctrl+press is the secondary click — no menu on the grip, so the drag can go on
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => {
                // owner 16:15: Ctrl / ⌘ on the first line of an equipment section — the whole section, no question
                if (leads(sec, l) && (e.ctrlKey || e.metaKey)) {
                  startSectionDrag(e, sec);
                  return;
                }
                // several lines selected: the order within the section does not move (19:46); the selected lines of one
                // equipment section go together into another section (10:47) — any other selection drags nothing
                let ids = [l.id];
                if (selected.length > 1) {
                  const together = sec.kind === 'equipment' && selected.includes(l.id) && selected.every((id) => sec.lines.some((x) => x.id === id));
                  if (!together) {
                    e.preventDefault();
                    return;
                  }
                  ids = sec.lines.filter((x) => selected.includes(x.id)).map((x) => x.id);
                }
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', l.id);
                setDrag({ sectionId: sec.id, id: l.id, ids, cross: sec.kind === 'equipment' });
              }}
              onDragEnd={endDrag}
            />
          ),
          // the order within the section, for one line; into another section: its <tbody> (`sectionDrop`, 10:47)
          onDragOver: (e: DragEvent<HTMLTableRowElement>) => {
            if (!drag || drag.sectionId !== sec.id || drag.ids.length > 1) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            const box = e.currentTarget.getBoundingClientRect();
            const after = e.clientY > box.top + box.height / 2;
            if (dropAt?.id !== l.id || dropAt.after !== after) setDropAt({ id: l.id, after });
          },
          onDrop: (e: DragEvent<HTMLTableRowElement>) => {
            if (!drag || drag.sectionId !== sec.id || drag.ids.length > 1) return;
            e.preventDefault();
            const box = e.currentTarget.getBoundingClientRect();
            reorder(sec.id, dropOn(sec.lines.map((x) => x.id), drag.id, l.id, e.clientY > box.top + box.height / 2));
            setDrag(null);
            setDropAt(null);
          },
        }
      : undefined;

  /**
   * The section's `<tbody>` as a drop target: of a dragged section (Ctrl / ⌘ + its first line's ⋮⋮, owner 16:15) only
   * before or after a whole section (owner 20:46);
   * of a line dragged from ANOTHER equipment section (10:47) before / after the line under the pointer, or at the end
   * (below its lines, its "Dodaj…" row, an empty section's row). „Obsługa” and „Transport” are no target of either.
   */
  const sectionDrop = (s: Section) => {
    if (!editable || s.kind !== 'equipment') return {};
    const after = (e: DragEvent<HTMLTableSectionElement>) => {
      const box = e.currentTarget.getBoundingClientRect();
      return e.clientY > box.top + box.height / 2;
    };
    const crossing = () => !!drag && drag.cross && drag.sectionId !== s.id;
    /** The line under the pointer and its half; none (the end): the last line's bottom edge, or the empty section. */
    const lineAt = (e: DragEvent<HTMLTableSectionElement>): { id: string; after: boolean } | null => {
      const row = e.target instanceof Element ? e.target.closest<HTMLElement>('tr[data-line-id]') : null;
      const id = row?.getAttribute('data-line-id');
      if (row && id && s.lines.some((l) => l.id === id)) {
        const box = row.getBoundingClientRect();
        return { id, after: e.clientY > box.top + box.height / 2 };
      }
      const last = s.lines[s.lines.length - 1];
      return last ? { id: last.id, after: true } : null;
    };
    return {
      className: [
        selSection === s.id ? 'q-section-selected' : '',
        secDrag === s.id ? 'q-sec-dragging' : '',
        secDrag && secDrag !== s.id && secDropAt?.id === s.id ? `q-sec-drop-${secDropAt.after ? 'after' : 'before'}` : '',
        dropEnd === s.id ? 'q-line-drop-into' : '',
      ]
        .filter(Boolean)
        .join(' '),
      onDragOver: (e: DragEvent<HTMLTableSectionElement>) => {
        if (secDrag) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const a = after(e);
          if (secDropAt?.id !== s.id || secDropAt.after !== a) setSecDropAt({ id: s.id, after: a });
          return;
        }
        if (!crossing()) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const at = lineAt(e);
        if (at ? dropAt?.id !== at.id || dropAt.after !== at.after : dropAt !== null) setDropAt(at);
        if (dropEnd !== (at ? null : s.id)) setDropEnd(at ? null : s.id);
      },
      onDrop: (e: DragEvent<HTMLTableSectionElement>) => {
        if (secDrag) {
          e.preventDefault();
          reorderSections(dropOn(equipmentIds, secDrag, s.id, after(e)));
          setSecDrag(null);
          setSecDropAt(null);
          setSecTip(null);
          return;
        }
        if (!drag || !crossing()) return;
        e.preventDefault();
        const at = lineAt(e);
        setMoving({ lineIds: drag.ids, sectionId: s.id, beforeLineId: landing(s.lines.map((l) => l.id), at?.id ?? null, at?.after ?? false) });
        setDrag(null);
        setDropAt(null);
        setDropEnd(null);
      },
    };
  };

  async function addSection(name = newName, categoryId: string | null = null) {
    try {
      await api(`/api/quotes/versions/${version.id}/sections`, { method: 'POST', body: { name, ...(categoryId ? { categoryId } : {}) } });
      setNewName('');
      setAdding(false);
      setError('');
      h.onRefresh();
    } catch (e) {
      setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się dodać sekcji');
    }
  }
  async function removeSection(s: Section) {
    try {
      await api(`/api/quotes/sections/${s.id}`, { method: 'DELETE' });
      setError('');
      setSelSection(null);
      h.onRefresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć sekcji');
    }
  }

  // owner 19:57: no „+ Sekcja” on the bar — an empty row at the end of the equipment sections: „Dodaj sekcję” in the
  // section column, shown on hover like "Dodaj…" (#114); a click on the row opens the same naming as before, in place
  const newSectionForm = (
    <form
      className="q-new-section"
      onSubmit={(e) => {
        e.preventDefault();
        void addSection();
      }}
    >
      {suggestions.length > 0 && (
        <div className="q-section-suggest" role="group" aria-label="Sekcja z kategorii katalogu" data-feature-id="quotes.category_sections">
          <span className="q-section-suggest-label">Z katalogu:</span>
          {suggestions.map((c) => (
            <button key={c.id} className="btn small" type="button" title={c.label} onClick={() => void addSection(c.name, c.id)}>
              {c.label}
            </button>
          ))}
        </div>
      )}
      <label className="field">
        <span>{suggestions.length > 0 ? 'Albo wpisz nazwę nowej sekcji' : 'Nazwa nowej sekcji'}</span>
        <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="np. Mikrofony" autoComplete="off" />
      </label>
      <div className="actions">
        <button className="btn small" type="submit">
          Dodaj sekcję
        </button>
        <button
          className="btn small ghost"
          type="button"
          onClick={() => {
            setAdding(false);
            setError(''); // owner 20:42: cancelling takes the message away too
          }}
        >
          Anuluj
        </button>
      </div>
    </form>
  );
  const addSectionRow = editable && (
    <tbody key="add-section" className="q-add-section" aria-label="Nowa sekcja" data-feature-id="quotes.category_sections">
      {adding ? (
        <tr className="q-new-section-row">
          <td colSpan={columnCount}>{newSectionForm}</td>
        </tr>
      ) : (
        <tr className="sheet-add sheet-add-click q-add-section-row" onClick={() => setAdding(true)}>
          <th scope="row" className="sheet-section-cell">
            <button className="q-add-section-btn" type="button">
              Dodaj sekcję
            </button>
          </th>
          <td colSpan={columnCount - 1} />
        </tr>
      )}
    </tbody>
  );
  const hasEquipment = parts.some((p) => p.group === 'equipment');

  return (
    <div className="q-sheet" data-feature-id="quotes.lines" ref={sheet} onKeyDown={onSheetKey} onDragOverCapture={onSheetDragOver}>
      <div className="table-wrap">
        <table
          className="table sheet-table q-table"
          style={{ '--q-min': `${quoteTableMinRem({ price: cols.price, discount: discountCol, net: cols.net, cost: !!cols.cost, supplier: supplierCol, actions: editable }, nameRem)}rem` } as CSSProperties}
        >
          <thead>
            <tr>
              <th scope="col" className="sheet-section-col q-col-section">Urządzenie / Usługa</th>
              <th scope="col" className="q-col-name">Marka</th>
              {cols.price && <th scope="col" className="num q-col-price">Cena</th>}
              <th scope="col" className="num q-col-qty">Ilość</th>
              <th scope="col" className="num q-col-days">Ilość dni</th>
              {discountCol && <th scope="col" className="num q-col-discount">Rabat %</th>}
              {cols.net && <th scope="col" className="num q-col-net">Kwota netto</th>}
              {cols.cost && (
                <th scope="col" className="num q-col-cost" title="Koszt dla firmy — widzisz go tylko Ty (i osoby z prawem do rentowności); nie trafia do PDF">
                  Koszt
                </th>
              )}
              {supplierCol && (
                <th scope="col" className="q-col-supplier" title="Od kogo dopożyczamy — widzisz to tylko Ty (i osoby z prawem do kosztów); nie trafia do PDF">
                  Dostawca
                </th>
              )}
              <th scope="col" className="q-col-desc">Opis</th>
              {editable && (
                <th scope="col" className="q-col-actions">
                  <span className="visually-hidden">Działania</span>
                </th>
              )}
            </tr>
          </thead>
          {!hasEquipment && addSectionRow}
          {parts.map((part) => [
            // P8: a clear line between the equipment and "Obsługa" / "Transport"
            part.group === 'services' && (
              <tbody key="below-line" className="q-below-line" aria-hidden="true">
                <tr>
                  <td colSpan={columnCount} />
                </tr>
              </tbody>
            ),
            ...part.sections.map((s) => {
              const fixed = s.kind !== 'equipment';
              const sum = fixed ? sectionSum(s, cols) : null;
              return (
                <SheetSection key={s.id} name={s.name} sectionId={s.id} {...sectionDrop(s)}>
                  {s.lines.map((l, i) => (
                    <QuoteLineRow
                      key={l.id}
                      line={l}
                      eventId={eventId}
                      sectionCell={
                        i === 0 ? <SectionName section={s} editable={editable && !fixed} onRefresh={h.onRefresh} /> : null
                      }
                      dayPctsBp={version.dayPctsBp}
                      cols={shownCols}
                      editable={editable}
                      onPreview={h.onPreview}
                      onSaved={h.onLineSaved}
                      onDeleted={h.onLineDeleted}
                      onRemove={(line) => setAsking({ lines: [line] })}
                      onSubrent={onSubrent}
                      select={selectOf(s, l)}
                      openCatalog={((t) => (mayOpen(me, t) ? () => openInCatalog(t, l.name) : undefined))(lineTarget(l))}
                      onConflict={h.onRefresh}
                      onAddToCatalog={mayAddToCatalog && !fixed && l.type === 'custom' && !l.supplierId ? (line) => setToCatalog({ seed: { name: line.name, categoryId: s.categoryId ?? null, priceGrosze: line.unitPriceGrosze ?? null }, line, sectionId: s.id, quantity: line.quantity }) : undefined}
                      notice={placementOf(l)}
                      onReplaceStart={mayReplace(l) ? () => setReplacing(l.id) : undefined}
                      replacing={
                        mayReplace(l) && replacing === l.id ? (
                          <ReplaceLine
                            line={l}
                            section={s}
                            versionId={version.id}
                            gear={gearOf(s.id)}
                            onReplaced={lineReplaced}
                            onCancel={() => {
                              setReplacing(null);
                              focusTo.current = l.id;
                            }}
                          />
                        ) : undefined
                      }
                    />
                  ))}
                  {s.lines.length === 0 && editable && !fixed ? (
                    // owner 19:46: no „Pusta sekcja.” — the section's own "Dodaj…" row stands by its name (the only
                    // add row); a click outside the field selects the section (Delete asks), × at the end asks too
                    <tr
                      className={`sheet-add sheet-add-click q-empty-section${selSection === s.id ? ' q-selected' : ''}`}
                      data-section-id={s.id}
                      tabIndex={-1}
                      aria-selected={selSection === s.id}
                      onClick={(e) => {
                        if (e.target instanceof Element && e.target.closest(OWN_CLICK)) return;
                        if (e.target instanceof Element && e.target.closest('.q-add-cell')) {
                          e.currentTarget.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
                          return;
                        }
                        setSelected([]);
                        setSelSection(s.id);
                        e.currentTarget.focus({ preventScroll: true });
                      }}
                    >
                      <th scope="row" className="sheet-section-cell">
                        <SectionName section={s} editable onRefresh={h.onRefresh} />
                      </th>
                      <td className="q-add-cell">
                        <AddPricedLine
                          versionId={version.id}
                          sectionId={s.id}
                          sectionName={s.name}
                          categoryId={s.categoryId ?? null}
                          onAdded={(line) => {
                            if (document.activeElement instanceof HTMLElement && document.activeElement.closest('.q-add-cell')) focusAdd.current = s.id;
                            h.onLineAdded(line);
                          }}
                          onNewItem={mayAddToCatalog ? (name, quantity) => setToCatalog({ seed: { name, categoryId: s.categoryId ?? null, priceGrosze: null }, sectionId: s.id, quantity }) : undefined}
                          gear={gearOf(s.id)}
                        />
                      </td>
                      <td colSpan={columnCount - 3} className="q-section-gap" />
                      <td className="q-actions">
                        <button className="btn icon ghost q-remove q-section-remove" type="button" aria-label="Usuń sekcję" title="Usuń sekcję" onClick={() => setAsking({ section: s })}>
                          ×
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {s.lines.length === 0 && !editable && (
                        <tr className="sheet-empty">
                          <th scope="row" className="sheet-section-cell">
                            <SectionName section={s} editable={false} onRefresh={h.onRefresh} />
                          </th>
                          <td colSpan={columnCount - 1} />
                        </tr>
                      )}
                      {editable && (
                        // owner 2026-10-23 16:17 („zachodzi na cenę”): the "Dodaj…" field stays in "Marka"; "+ Usługa" may run on.
                        // 16:20: „+ Usługa” / „+ Transport” right under the last line, before „Razem …” (quotes.css .q-fixed-actions)
                        // 19:52: an empty „Obsługa” / „Transport” shows no text — its name stands by this row
                        <SheetAddRow
                          span={fixed ? columnCount - 1 : 1}
                          after={fixed ? 0 : columnCount - 2}
                          focusOnClick={!fixed}
                          head={s.lines.length === 0 ? <SectionName section={s} editable={false} onRefresh={h.onRefresh} /> : undefined}
                        >
                          {fixed ? (
                            <span className="q-fixed-actions">
                              <FixedSectionAdd
                                versionId={version.id}
                                section={s}
                                onAdded={(line, other) => {
                                  if (other) focusName.current = line.id;
                                  h.onLineAdded(line);
                                }}
                              />
                            </span>
                          ) : (
                            <AddPricedLine
                              versionId={version.id}
                              sectionId={s.id}
                              sectionName={s.name}
                              categoryId={s.categoryId ?? null}
                              onAdded={h.onLineAdded}
                              onNewItem={mayAddToCatalog ? (name, quantity) => setToCatalog({ seed: { name, categoryId: s.categoryId ?? null, priceGrosze: null }, sectionId: s.id, quantity }) : undefined}
                              gear={gearOf(s.id)}
                            />
                          )}
                        </SheetAddRow>
                      )}
                    </>
                  )}
                  {sum !== null && (
                    <tr className="q-section-total">
                      <th scope="row" colSpan={beforeNet}>
                        Razem {s.name.toLocaleLowerCase('pl-PL')}
                      </th>
                      <td className="num q-net">
                        <span className="q-text">{formatPln(sum)}</span>
                      </td>
                      <td colSpan={afterNet} />
                    </tr>
                  )}
                </SheetSection>
              );
            }),
            part.group === 'equipment' && addSectionRow,
            // template 2: "Łącznie urządzenia" / "Łącznie po rabacie (25%)" under the equipment, "Łącznie usługi" under the services
            part.summary.length > 0 && (
              <tbody key={`total-${part.group}`} className={`q-part-total q-part-${part.group}`} aria-label={part.group === 'services' ? 'Suma usług' : 'Suma urządzeń'}>
                {part.summary.map((r) => (
                  <tr key={r.label}>
                    <th scope="row" colSpan={beforeNet}>
                      {r.label}
                    </th>
                    <td className="num q-net">
                      <span className="q-text" data-testid={`part-${r.label}`}>
                        {formatPln(r.grosze)}
                      </span>
                    </td>
                    <td colSpan={afterNet} />
                  </tr>
                ))}
              </tbody>
            ),
          ])}
        </table>
      </div>
      {notice && (
        <p className="hint" role="status">
          {notice}
        </p>
      )}
      {/* a deleted line can be brought back: „Cofnij” (⌘Z) */}
      {removed.length > 0 && (
        <p className="hint q-undo" role="status" tabIndex={-1}>
          {removed.length === 1 ? `Usunięto pozycję „${removed[0]!.line.name}”` : `Usunięto ${removed.length} ${linesWord(removed.length)}`}
          {removed.some((r) => undoable(r.line)) ? (
            <>
              {removed.every((r) => undoable(r.line)) ? '' : ' (pozycji od dostawcy nie da się przywrócić — dodaj je ponownie z katalogu dostawcy)'}
              {' — '}
              <button className="btn small ghost" type="button" onClick={undo} title={`Cofnij (${isMac() ? '⌘Z' : 'Ctrl+Z'})`}>
                Cofnij
              </button>
            </>
          ) : (
            `. ${removed.length === 1 ? 'Jej dopożyczenie też usunięto — dodaj ją' : 'Ich dopożyczenia też usunięto — dodaj je'} ponownie z katalogu dostawcy.`
          )}
        </p>
      )}
      {/* owner 19:48: every delete asks first; Usuń has the focus, Esc / Anuluj leave everything as it was */}
      {asking && (
        <div className="dialog-backdrop">
          <div
            className="dialog panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="q-delete-title"
            onKeyDown={(e) => {
              if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
            }}
          >
            <DialogLayer onClose={() => setAsking(null)} />
            <h2 id="q-delete-title">{askTitle(asking)}</h2>
            <div className="actions">
              <button
                className="btn primary q-danger"
                type="button"
                autoFocus
                onClick={() => {
                  const a = asking;
                  setAsking(null);
                  if ('section' in a) void removeSection(a.section);
                  else void deleteLines(a.lines);
                }}
              >
                Usuń
              </button>
              <button className="btn" type="button" onClick={() => setAsking(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {/* owner 2026-10-24 10:47: a move into another section asks first; Przenieś has the focus, Esc / Anuluj keep it */}
      {moving && (
        <div className="dialog-backdrop">
          <div
            className="dialog panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="q-move-title"
            data-feature-id="quotes.line_order"
            onKeyDown={(e) => {
              if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
            }}
          >
            <DialogLayer onClose={() => setMoving(null)} />
            <h2 id="q-move-title">
              {moveTitle(
                moving,
                flat.filter((x) => moving.lineIds.includes(x.line.id)).map((x) => x.line),
                shown.find((x) => x.id === moving.sectionId),
              )}
            </h2>
            <div className="actions">
              <button
                ref={moveButton}
                className="btn primary"
                type="button"
                autoFocus
                onClick={() => {
                  const m = moving;
                  setMoving(null);
                  moveLines(m);
                }}
              >
                Przenieś
              </button>
              <button className="btn" type="button" onClick={() => setMoving(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {inserting && me && (
        <InsertBlockDialog
          me={me}
          version={version}
          onDone={(message) => {
            setInserting(false);
            setNotice(message);
            h.onRefresh();
          }}
          onClose={() => setInserting(false)}
        />
      )}
      {toCatalog && <NewCatalogItemDialog seed={toCatalog.seed} onCreated={catalogItemCreated} onClose={() => setToCatalog(null)} />}
      {subrent && eventId && (
        <RentalForm
          eventId={eventId}
          preset={subrent}
          onClose={() => {
            setSubrent(null);
            // the rental is the event's: the quote reads its line (suppliers, cost, what is still missing) again
            h.onRefresh();
          }}
        />
      )}
      {error && (
        <p className="alert" role="alert" ref={errorRef}>
          {error}
        </p>
      )}
      {/* owner 16:15: „musi pokazac podpowiedz w dymku” — in the body: the sheet (a size container) would hold it */}
      {secTip &&
        createPortal(
          <div className="hint-pop q-drag-tip" role="status" style={{ left: secTip.x + 16, top: secTip.y + 16 }}>
            {`Przenosisz całą sekcję „${secTip.name}”`}
          </div>,
          document.body,
        )}
    </div>
  );
}
